import Protected from "@/pages/protected";
import { DataService } from "@/services/data";
import { ExternalRegistrationType } from "@/types/external_registration/registration";
import { handle_error } from "@/utils/wretch";
import { Outlet, RouteObject, defer, json } from "react-router-dom";

const ExternalRegistrationRoute: RouteObject = {
    id: "external_registration-root",
    path: "externes",
    element: (
        <Protected groups={["app_registration_admin", "team_finance", "team_external_provider"]} profiles={["system_admin"]}>
            <Outlet />
        </Protected>
    ),
    loader: async () => {
        const providers = await DataService.client.url(`/registration/sales/providers/?page_size=1000`).get().json();
        return defer({ providers });
    },
    children: [
        {
            index: true,
            async lazy() {
                let { ListPage } = await import("@/pages/registration/external_registration/list");
                return { Component: ListPage };
            },
            loader: async ({ request }) => {
                const url = new URL(request.url);
                if (!url.searchParams.has("page_size")) {
                    url.searchParams.set("page_size", "25");
                }
                const registrations = await DataService.client
                    .url(`/registration/sales/external/${url.searchParams ? "?" + url.searchParams.toString() : ""}`)
                    .get()
                    .json();
                return defer({ registrations });
            }
        },
        {
            path: "nouveau",
            action: async ({ request }) => {
                const data = await request.json();
                const url = new URL(request.url);
                const action = url.searchParams.get("action");
                try {
                    if (action) {
                        const content = await DataService.client.url(`/registration/sales/external/${action}/`).post(data).json();
                        return json({ success: true, content });
                    }
                    const registration = (await DataService.client.url(`/registration/sales/external/`).post(data).json()) as ExternalRegistrationType;
                    return json({ success: true, content: registration });
                } catch (error) {
                    return handle_error(error);
                }
            }
        },
        {
            path: "utilisateurs",
            async lazy() {
                let { UsersPage } = await import("@/pages/registration/external_registration/users");
                return { Component: UsersPage };
            },
            loader: async ({ request }) => {
                const url = new URL(request.url);
                const providerId = url.searchParams.get("provider_id");
                if (!providerId) {
                    return { linkedUsers: [], linkedUsersError: null };
                }
                try {
                    const linkedUsers = await DataService.client.url(`/registration/sales/providers/${providerId}/linked_users/`).get().json();
                    return { linkedUsers, linkedUsersError: null };
                } catch (error: any) {
                    const status = error?.response?.status;
                    const message =
                        status === 403
                            ? "Vous n'avez pas la permission de consulter les utilisateurs de ce fournisseur"
                            : "Impossible de charger les utilisateurs liés";
                    return { linkedUsers: [], linkedUsersError: message };
                }
            },
            action: async ({ request }) => {
                const data = await request.json();
                const url = new URL(request.url);
                const actionName = url.searchParams.get("action");
                const providerId = url.searchParams.get("provider_id");
                if (!providerId || !actionName) {
                    return json({ success: false, message: "Missing provider or action" });
                }
                try {
                    const content = await DataService.client.url(`/registration/sales/providers/${providerId}/${actionName}/`).post(data).json();
                    return json({ success: true, content });
                } catch (error) {
                    return handle_error(error);
                }
            }
        },
        {
            path: ":registrationId",
            loader: async ({ params }) => {
                const registrationId = params.registrationId;
                const registration = await DataService.client.url(`/registration/sales/external/${registrationId}/`).get().json();
                return registration;
            },
            action: async ({ params, request }) => {
                const registrationId = params.registrationId;
                const data = await request.json();
                const url = new URL(request.url);
                const action = url.searchParams.get("action");
                if (action) {
                    try {
                        const content = await DataService.client.url(`/registration/sales/external/${registrationId}/${action}/`).post(data).json();
                        return json({ success: true, content: content });
                    } catch (error) {
                        return handle_error(error);
                    }
                } else {
                    try {
                        const content = await DataService.client.url(`/registration/sales/external/${registrationId}/`).patch(data).json();
                        return json({ success: true, content: content });
                    } catch (error) {
                        return handle_error(error);
                    }
                }
            }
        },
        {
            path: ":registrationId/related",
            loader: async ({ params }) => {
                const registrationId = params.registrationId;
                const registration = await DataService.client.url(`/registration/sales/external/${registrationId}/related_registration/`).get().json();
                return registration;
            }
        },
        {
            path: ":registrationId/installment_schedule",
            loader: async ({ params }) => {
                const registrationId = params.registrationId;
                return await DataService.client.url(`/registration/sales/external/${registrationId}/related_installment_schedule/`).get().json();
            }
        }
    ]
};
export default ExternalRegistrationRoute;
